/**
@license
Copyright (c) 2015 The Polymer Project Authors. All rights reserved.
This code may only be used under the BSD style license found at
http://polymer.github.io/LICENSE.txt The complete set of authors may be found at
http://polymer.github.io/AUTHORS.txt The complete set of contributors may be
found at http://polymer.github.io/CONTRIBUTORS.txt Code distributed by Google as
part of the polymer project is also subject to an additional IP rights grant
found at http://polymer.github.io/PATENTS.txt
*/
import '@polymer/polymer/polymer-legacy.js';
import { IronMeta } from '@polymer/iron-meta/iron-meta.js';
import { Polymer } from '@polymer/polymer/lib/legacy/polymer-fn.js';
import { dom } from '@polymer/polymer/lib/legacy/polymer.dom.js';
/**
 * The `iron-iconset` element allows users to define their own icon set using
 * an image file. (To create an iconset using SVG icons, see
 * [`iron-iconset-svg`](iron-iconset-svg).)
 *
 * The `src` property specifies the url of the icon image. Multiple icons may
 * be included in this image and they may be organized into rows. The `icons`
 * property is a space separated list of names corresponding to the icons. The
 * names must be ordered as the icons are ordered in the icon image. Icons are
 * expected to be square and are the size specified by the `size` property. The
 * `width` property corresponds to the width of the icon image and must be
 * specified if icons are arranged into multiple rows in the image.
 *
 * All `iron-iconset` elements are available for use by other `iron-iconset`
 * elements via a database keyed by id. Typically, an element author that wants
 * to support a set of custom icons uses a `iron-iconset` to retrieve and use
 * another, user-defined icon set, or simply uses the [`iron-icon`](iron-icon)
 * element to display an icon by specifying its icon set name and id.
 *
 * Example:
 *
 *     <iron-iconset id="my-icons" src="my-icons.png" width="96" size="24"
 *         icons="location place starta stopb bus car train walk">
 *     </iron-iconset>
 *
 * This will automatically register the icon set "my-icons" to the iconset
 * database. To use these icons from within another element, make a
 * `iron-iconset` element and call the `byId` method to retrieve a given
 * iconset. To apply a particular icon to an element, use the `applyIcon`
 * method. For example:
 *
 *     iconset.applyIcon(iconNode, 'car');
 *
 * Themed icon sets are also supported. The `iron-iconset` can contain child
 * `property` elements that specify a theme with an offsetX and offsetY of the
 * theme within the icon resource. For example.
 *
 *     <iron-iconset id="my-icons" src="my-icons.png" width="96" size="24"
 *         icons="location place starta stopb bus car train walk">
 *       <property theme="special" offsetX="256" offsetY="24"></property>
 *     </iron-iconset>
 *
 * Then a themed icon can be applied like this:
 *
 *     iconset.applyIcon(iconNode, 'car', 'special');
 *
 * See also:
 *
 * - [`iron-iconset-svg`](iron-iconset-svg). Build icon sets with SVG elements.
 * - [`iron-icons`](iron-icons). Predefined icon sets.
 * - [`iron-icon`](iron-icon). Simple element to display an icon.
 *
 * @demo demo/index.html
 * @implements {Polymer.Iconset}
 */

Polymer({
  is: 'iron-iconset',
  properties: {
    /**
     * The URL of the iconset image.
     */
    src: {
      type: String,
      observer: '_srcChanged'
    },

    /**
     * The name of the iconset.
     */
    name: {
      type: String,
      observer: '_nameChanged'
    },

    /**
     * The width of the iconset image. This must only be specified if the
     * icons are arranged into separate rows inside the image.
     *
     * @attribute width
     * @type number
     * @default 0
     */
    width: {
      type: Number,
      value: 0
    },

    /**
     * A space separated list of names corresponding to icons in the iconset
     * image file. This list must be ordered the same as the icon images
     * in the image file.
     */
    icons: {
      type: String
    },

    /**
     * The size of an individual icon. Note that icons must be square.
     */
    size: {
      type: Number,
      value: 24
    },

    /**
     * The horizontal offset of the icon images in the inconset src image.
     * This is typically used if the image resource contains additional images
     * beside those intended for the iconset.
     */
    _offsetX: {
      type: Number,
      value: 0
    },

    /**
     * The vertical offset of the icon images in the inconset src image.
     * This is typically used if the image resource contains additional images
     * beside those intended for the iconset.
     */
    _offsetY: {
      type: Number,
      value: 0
    },

    /**
     * Array of fully-qualified names of icons in this set.
     */
    iconNames: {
      type: Array,
      notify: true
    }
  },
  hostAttributes: {
    // non-visual
    style: 'display: none;'
  },
  created: function created() {
    this._meta = new IronMeta({
      type: 'iconset',
      key: null,
      value: null
    });
  },
  ready: function ready() {
    // theme data must exist at ready-time
    this._themes = this._mapThemes();
  },

  /**
   * Applies an icon to the given element as a css background image. This
   * method does not size the element, and it's usually necessary to set
   * the element's height and width so that the background image is visible.
   *
   * @param {Element} element The element to which the icon is applied.
   * @param {string|number} icon The name or index of the icon to apply.
   * @param {string=} theme (optional) The name or index of the icon to apply.
   * @param {number=} scale (optional, defaults to 1) Icon scaling factor.
   */
  applyIcon: function applyIcon(element, icon, theme, scale) {
    this._validateIconMap();

    var offset = this._getThemedOffset(icon, theme);

    if (element && offset) {
      this._addIconStyles(element, this._srcUrl, offset, scale || 1, this.size, this.width);
    }
  },

  /**
   * Remove an icon from the given element by undoing the changes effected
   * by `applyIcon`.
   *
   * @param {Element} element The element from which the icon is removed.
   */
  removeIcon: function removeIcon(element) {
    this._removeIconStyles(element.style);
  },
  _mapThemes: function _mapThemes() {
    var themes = Object.create(null);
    dom(this).querySelectorAll('property[theme]').forEach(function (property) {
      var offsetX = window.parseInt(property.getAttribute('offset-x'), 10) || 0;
      var offsetY = window.parseInt(property.getAttribute('offset-y'), 10) || 0;
      themes[property.getAttribute('theme')] = {
        offsetX: offsetX,
        offsetY: offsetY
      };
    });
    return themes;
  },
  _srcChanged: function _srcChanged(src) {
    // ensure `srcUrl` is always relative to the main document
    this._srcUrl = this.ownerDocument !== document ? this.resolveUrl(src) : src;

    this._prepareIconset();
  },
  _nameChanged: function _nameChanged(name) {
    this._prepareIconset();
  },
  _prepareIconset: function _prepareIconset() {
    this._meta.value = null;
    this._meta.key = this.name;
    this._meta.value = this;
    this.async(function () {
      this.fire('iron-iconset-added', this, {
        node: window
      });
    });
  },
  _invalidateIconMap: function _invalidateIconMap() {
    this._iconMapValid = false;
  },
  _validateIconMap: function _validateIconMap() {
    if (!this._iconMapValid) {
      this._recomputeIconMap();

      this._iconMapValid = true;
    }
  },
  _recomputeIconMap: function _recomputeIconMap() {
    this.iconNames = this._computeIconNames(this.icons);
    this.iconMap = this._computeIconMap(this._offsetX, this._offsetY, this.size, this.width, this.iconNames);
  },
  _computeIconNames: function _computeIconNames(icons) {
    return icons.split(/\s+/g);
  },
  _computeIconMap: function _computeIconMap(offsetX, offsetY, size, width, iconNames) {
    var iconMap = {};

    if (offsetX !== undefined && offsetY !== undefined) {
      var x0 = offsetX;
      iconNames.forEach(function (iconName) {
        iconMap[iconName] = {
          offsetX: offsetX,
          offsetY: offsetY
        };

        if (offsetX + size < width) {
          offsetX += size;
        } else {
          offsetX = x0;
          offsetY += size;
        }
      }, this);
    }

    return iconMap;
  },

  /**
   * Returns an object containing `offsetX` and `offsetY` properties which
   * specify the pixel location in the iconset's src file for the given
   * `icon` and `theme`. It's uncommon to call this method. It is useful,
   * for example, to manually position a css backgroundImage to the proper
   * offset. It's more common to use the `applyIcon` method.
   *
   * @param {string|number} identifier The name of the icon or the index of
   * the icon within in the icon image.
   * @param {string=} theme The name of the theme.
   * @returns {?{offsetX: number, offsetY: number}} An object specifying the
   *     offset of the given icon within the icon resource file; `offsetX` is
   *     the horizontal offset and `offsetY` is the vertical offset. Both
   *     values are in pixel units.
   */
  _getThemedOffset: function _getThemedOffset(identifier, theme) {
    var iconOffset = this._getIconOffset(identifier);

    var themeOffset = this._themes[theme];

    if (iconOffset && themeOffset) {
      return {
        offsetX: iconOffset.offsetX + themeOffset.offsetX,
        offsetY: iconOffset.offsetY + themeOffset.offsetY
      };
    }

    return iconOffset;
  },
  _getIconOffset: function _getIconOffset(identifier) {
    // TODO(sjmiles): consider creating offsetArray (indexed by Number)
    // and having iconMap map names to indices, then and index is just
    // iconMap[identifier] || identifier (be careful of zero, store indices
    // as 1-based)
    return this.iconMap[identifier] || this.iconMap[this.iconNames[Number(identifier)]];
  },
  _addIconStyles: function _addIconStyles(element, url, offset, scale, size, width) {
    var style = element.style;
    style.backgroundImage = 'url(' + url + ')';
    style.backgroundPosition = -offset.offsetX * scale + 'px' + ' ' + (-offset.offsetY * scale + 'px');
    style.backgroundSize = scale === 1 ? 'auto' : width * scale + 'px';
    style.width = size + 'px';
    style.height = size + 'px';
    element.setAttribute('role', 'img');
  },
  _removeIconStyles: function _removeIconStyles(style) {
    style.background = '';
  }
});